import { useRef, useEffect, useCallback, useMemo } from "react";
// import userList from "src/assets/user-info.json";
import get from "src/shared/utils/safeGet";
import { IReduxOperations } from "src/store";
import NS, { NSStatus } from "./NS";
export { default as browserName } from "./browser-detect";
export * from "./abbreviateNumber";
export { default as safeGet } from "./safeGet";
export { default as TimeDifferenceGA } from "./ReactGaCustom";
/**
 *
 * input==> [[12,23],[23,34],[45,56]]
 * returns==>[{x:12,y:23},{x:23,y:34},{x:45,y:56}]
 */
// export function parseData(data: Array<Array<number>>) {
//   const mapping = data ? data.map(item => ({ x: item[0], y: item[1] })) : [];
//   return mapping;
// }

export const cr = "\n";
export const tab = "\t";

export const parseData = (data: Array<Array<number>>) => (data ? data.map((item) => ({ x: item[0], y: item[1] })) : []);

/**
 * calculates big numbers based on chart plots for
 * big number type charts
 */
export interface ChartData {
  data: number[][];
  compare_lag: number;
  body?: any;
}
export function calculateData(data: ChartData) {
  let vCompare = 0;
  const bigNumber = get(data, `data[${get(data, "data.length", 1) - 1}]`, [])[1];
  if (data.compare_lag > 0) {
    const pos = data.data.length - (data.compare_lag + 1);
    if (pos >= 0) {
      const vAnchor = data.data[pos][1];
      if (vAnchor !== 0) {
        vCompare = (bigNumber - vAnchor) / Math.abs(vAnchor);
      } else {
        vCompare = 0;
      }
    }
  }
  return { bigNumber, vCompare };
}

export type DateOut = {
  date: string;
  month: string;
};

/**
 * utility to format date object and extract date and month
 * big number type charts
 * Input -> Date string
 * Ouput -> object containing date and month properties
 */
const monthStack = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const dateUtility = (dateString: string): DateOut => {
  let displayDate;
  try {
    displayDate = new Date(dateString);
    const date = displayDate.getDate();

    return {
      date: date + (date > 0 ? ["th", "st", "nd", "rd"][(date > 3 && date < 21) || date % 10 > 3 ? 0 : date % 10] : ""),
      month: monthStack[displayDate.getMonth()],
    };
  } catch (e) {
    return (displayDate = {
      date: "",
      month: "",
    });
  }
};
//function to rearrange rows in an array
export const rearrangeRowsinList = (notesListArray: any, initialIndex: number, finalIndex: number) => {
  if (
    initialIndex >= 0 &&
    initialIndex <= notesListArray.length &&
    finalIndex <= notesListArray.length &&
    finalIndex >= 0
  ) {
    const tempElement = notesListArray[initialIndex];
    notesListArray.splice(initialIndex, 1);
    notesListArray.splice(finalIndex, 0, tempElement);
    return notesListArray;
  }
};

export const noop = () => {};

export const parseJson = (data: string) => {
  try {
    return JSON.parse(data);
  } catch (e) {
    // console.error(e);
    return [];
  }
};

export function getPreviousState(path: string) {
  const store = localStorage.getItem("store");
  if (store) {
    try {
      const parsedStore = JSON.parse(store);
      return get(parsedStore, path, undefined);
    } catch (e) {
      return undefined;
    }
  }
  return undefined;
}

/** Implentation of Pure shouldComponentUpdate */
export const useEffectOnPropsChange = (fn: () => void, deps: Array<any>) => {
  const hasMounted = useRef(false);
  useEffect(() => {
    if (hasMounted.current) {
      fn();
    } else {
      hasMounted.current = true;
    }
  }, deps);
};

export function checkIfIOSDevice() {
  return !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform);
}

export function isMobileDevice() {
  return typeof window.orientation !== "undefined" || navigator.userAgent.indexOf("IEMobile") !== -1;
}

// export function getUniqueId(userID: string) {
//   if (!userID) return "-1";
//   const currentUser = userList.find(user => user.email === userID);
//   return get(currentUser, "userId", "0") + "";
// }

export function getTimeFromTimestamp(time: number) {
  const date = new Date(Number.isNaN(time) ? Date.now() : time);
  const TIME_ZONE_OFFSET = date.getTimezoneOffset() * 60000; //19800000; // to offset time to current time zone
  const localDate = new Date(Number.isNaN(time + TIME_ZONE_OFFSET) ? Date.now() : time + TIME_ZONE_OFFSET);

  return localDate.toLocaleString("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
}

const LOCALSTORAGE_VALUE_KEY = "_useHiddenTime_value";

const initialValue = {
  lastHiddenAt: 0,
  hiddenTime: 0,
  monitorStartTime: 0,
  startTimestamp: 0,
};

function getInitialValue() {
  const s = localStorage.getItem(LOCALSTORAGE_VALUE_KEY) || "";
  localStorage.removeItem(LOCALSTORAGE_VALUE_KEY);
  try {
    const { monitorTime, ...v } = JSON.parse(s) as typeof initialValue & { monitorTime: number };
    v.lastHiddenAt = performance.now();
    v.monitorStartTime = -(performance.now() + monitorTime);
    return { ...v };
  } catch (error) {
    return { ...initialValue };
  }
}

export function useHiddenTime() {
  const initialValueMemoized = useMemo(getInitialValue, []);
  const { current: state } = useRef(initialValueMemoized);

  const handleVisibilityChange = useCallback(() => {
    if (document.hidden) {
      state.lastHiddenAt = performance.now();
    } else {
      state.hiddenTime += performance.now() - state.lastHiddenAt;
    }
  }, []);

  const startMonitor = () => {
    state.monitorStartTime = state.lastHiddenAt = performance.now();
    state.startTimestamp = Date.now();
    state.hiddenTime = 0;
    document.addEventListener("visibilitychange", handleVisibilityChange, false);
  };

  const stopMonitor = () => {
    // start not called
    if (state.lastHiddenAt === 0 || state.monitorStartTime === 0) {
      return { hiddenTime: 0, monitorTime: 0, visibleTime: 0, startTimestamp: 0, endTimestamp: 0 };
    }

    document.removeEventListener("visibilitychange", handleVisibilityChange, false);
    const monitorEndTime = performance.now();
    const endTimestamp = Date.now();
    if (document.hidden) state.hiddenTime += monitorEndTime - state.lastHiddenAt;

    const { hiddenTime, monitorStartTime, startTimestamp } = state;

    // reset to initial state
    // Can't assign directly like `state = initialValue`. It won't really update ref
    // Either we have to do this below way, or we can do `ref.current = initialValue`
    Object.entries(initialValue).forEach(([k, v]) => (state[k] = v));

    const monitorTime = monitorEndTime - monitorStartTime;
    return {
      startTimestamp,
      endTimestamp,
      hiddenTime,
      monitorTime: monitorTime,
      visibleTime: monitorTime - hiddenTime,
    };
  };

  useEffect(() => {
    window.addEventListener("beforeunload", () => {
      if (state.monitorStartTime > 0) {
        if (document.hidden) state.hiddenTime += performance.now() - state.lastHiddenAt;
        localStorage.setItem(
          LOCALSTORAGE_VALUE_KEY,
          JSON.stringify({ ...state, monitorTime: performance.now() - state.monitorStartTime })
        );
      }
    });
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange, false);
  }, []);

  // `as const` makes expression a tuple and preserve type information
  return [startMonitor, stopMonitor] as const;
}

export function reduxOperationToStatus(op: IReduxOperations<any>) {
  let status: NSStatus = "INIT";
  if (op.success) status = "SUCCESS";
  if (op.pending) status = "LOADING";
  if (op.failure) status = "ERROR";
  return new NS(status);
}

const SI_SYMBOL = ["", "K", "M", "G", "T", "P", "E"];
// later name of the function should be changed, temp fix for naming collision.
export function sharedUtilsAbbreviateNumber(number: number, fractionDigits = 1) {
  if (!Number.isFinite(number)) return String(number);

  // what tier? (determines SI symbol)
  const tier = (Math.log10(Math.abs(number)) / 3) | 0;

  // if zero, we don't need a suffix
  if (tier === 0) return number.toFixed(fractionDigits);

  // get suffix and determine scale
  const suffix = SI_SYMBOL[tier];
  const scale = Math.pow(10, tier * 3);

  // scale the number
  const scaled = number / scale;

  // format number and add suffix
  return scaled.toFixed(fractionDigits) + suffix;
}

export const truncate = (s?: string, n = 0) => {
  if (!s) return "";
  return s.length > n ? `${s.substring(0, n)}...` : s;
};

export const range = (n: number) => Array(n).fill(undefined);

export const toTitleCase = (s: string) =>
  s.replace(/\w\S*/g, (t: string) => t.charAt(0).toUpperCase() + t.substr(1).toLowerCase());

export function useDidUpdate(effect: () => unknown, dependencies: any[] = []) {
  const hasMounted = useRef(false);
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    effect();
  }, dependencies);
}

export const zip = (...rows) => rows[0].map((_, column) => rows.map((row) => row[column]));

export const clearCache = async () => {
  localStorage.clear();
  sessionStorage.clear();
  indexedDB.deleteDatabase("workbox-expiration");
  await caches.keys().then((cs) => Promise.all(cs.map((c) => caches.delete(c))));
};

// Checks if the given value is empty or not
export function isEmpty(value: any, ignoreWhitespace = true): boolean {
  switch (typeof value) {
    case "string":
      return ignoreWhitespace ? value.length === 0 : value.trim().length === 0;
    case "object":
      return value === null || Object.keys(value).length === 0; // handling for null.
    default:
      return !value;
  }
}

// https://stackoverflow.com/a/2117523
export function uuidv4(): string {
  // @ts-expect-error
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  );
}

export const mergeStatuses = (...statuses: NS[]): NS => {
  const hasData = statuses.some((s) => s.hasData);
  const statusWithError = statuses.find((s) => s.isError);
  const message = statusWithError ? statusWithError.message : "";
  const statusCode = statusWithError ? statusWithError.statusCode : 0;

  const status = statuses.every((s) => s.isSuccess)
    ? "SUCCESS" // [SUCCESS, SUCCESS, SUCCESS]
    : statusWithError
    ? "ERROR" // [SUCCESS, ERROR] or [LOADING, ERROR]
    : statuses.find((s) => s.isLoading)
    ? "LOADING" // [SUCCESS, LOADING] or [LOADING, INIT] or [LOADING, LOADING]
    : statuses.find((s) => s.isSuccess)
    ? "LOADING" // [SUCCESS, INIT]
    : "INIT"; // [INIT, INIT]

  return new NS(status, message, statusCode, 0, 0, "", false, hasData);
};
